import React from 'react'

import { Box } from 'ui/components/Box'
import { Dropdown, DropdownContent, DropdownItem, DropdownTrigger } from 'ui/components/Dropdown'
import { Icon } from 'ui/components/Icon'
import { stopPropagation } from 'ui/helpers/utilities'

import { useEditableImageFileState } from './hooks/useEditableImageFileState'
import { useEditableImageState } from './hooks/useEditableImageState'
import { EditableSection } from './section/EditableSection'

import {
    EditableImageButtonIconStyle,
    EditableImageButtonStyle,
    EditableImageStyle,
} from './EditableImage.css'

type EditableImageProps = {
    type: 'field' | 'image'

    // Type: field
    field?: FieldDto

    // Type: image
    value?: string
    onChange?: (url?: string, filename?: string) => void

    // Common
    disabled?: boolean
    size?: 'xs' | 's' | 'm' | 'l' | 'xl' | '2xl' | '3xl' | '4xl'
}

const sizeMap = {
    icon: {
        xs: 'xs',
        s: 's',
        m: 'm',
        l: 'l',
        xl: 'xl',
        '2xl': '4xl',
        '3xl': '4xl',
        '4xl': '4xl',
    },
} as const

export const EditableImage: React.FC<EditableImageProps> = ({
    type,
    field,
    disabled,
    size = '2xl',
    value,
    onChange,
}) => {
    const { isEditable, editableFields } = useEditableImageState({
        type,
        field,
        onChange,
        disabled,
    })

    if (!isEditable) return null

    const content = (
        <Box as="button" aria-disabled={disabled} className={EditableImageButtonStyle}>
            <Icon
                name="Camera"
                size={sizeMap.icon[size]}
                display="block"
                className={EditableImageButtonIconStyle}
            />
        </Box>
    )

    return (
        <Box
            flex
            center
            justifyContent="center"
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            width="full"
            height="full"
            className={EditableImageStyle}
        >
            {type === 'field' ? (
                <EditableSection wrapInButton={false} fields={editableFields}>
                    {content}
                </EditableSection>
            ) : (
                <EditableImageFile value={value!} onChange={onChange!}>
                    {content}
                </EditableImageFile>
            )}
        </Box>
    )
}

type EditableImageFileProps = Pick<EditableImageProps, 'value'> &
    Required<Pick<EditableImageProps, 'onChange'>> & {}

export const EditableImageFile: React.FC<EditableImageFileProps> = ({
    value,
    onChange,
    children,
}) => {
    const { onRemoveImage, onInteractOutside, onUploadClick } = useEditableImageFileState({
        onChange,
    })

    return (
        <Dropdown modal={false}>
            <DropdownTrigger asChild>{children}</DropdownTrigger>
            <DropdownContent
                style={{ width: '290px' }}
                align="start"
                side="bottom"
                onCloseAutoFocus={stopPropagation}
                onInteractOutside={onInteractOutside}
            >
                <DropdownItem
                    label="Upload image"
                    startIcon={{ name: 'Upload' }}
                    onClick={onUploadClick}
                />
                {!!value && (
                    <DropdownItem
                        label="Remove image"
                        startIcon={{ name: 'X' }}
                        variant="destructive"
                        onClick={onRemoveImage}
                    />
                )}
            </DropdownContent>
        </Dropdown>
    )
}
